import type { useTranslations } from 'next-intl';
import { GLOBAL_LANGUAGES } from '@/app/constants';
import type { I18nRecord } from '@/app/types';
import { QUESTIONS_CONFIG } from './Questions/utils';

const FAQ_SEO_ZH = [
  {
    question: '什么是 Vidu？',
    answer:
      'Vidu 是北京生数科技股份有限公司 联合清华大学发布的中国首个长时长、高一致性、高动态性视频大模型。Vidu 可以将文本和图片转化为高质量视频，支持多种创作模式，包括文生视频、图生视频和参考生视频。Vidu 专为独立创作者和团队打造，简化了动态视频、2D 动画及多种艺术风格的制作流程，让用户轻松实现创意表达。Vidu 具备强大的语义理解能力和高级镜头运动设计，消除了复杂的制作流程，让创作者可以专注于创意本身。从低分辨率到高清 1080p 输出，Vidu 提供极高的灵活性和效率，为创作提供更广阔的空间。',
  },
  {
    question: 'Vidu 的用途是什么？',
    answer:
      'Vidu AI 是一款强大的 AI 视频生成工具，以卓越的主体一致性和高速生成能力闻名，广泛应用于多个行业，包括社交媒体内容创作、专业视频制作和商业广告等。许多创作者使用 Vidu AI 生成病毒式传播的短视频，如亲吻、拥抱等热门内容。影视工作室和动画团队利用 Vidu AI 制作电影级动画和专业级内容。企业使用 Vidu AI 优化营销视频，例如通过图片转视频让产品图像动起来，或使用参考图转视频更改广告背景，使视频广告更加生动有趣。凭借先进的 AI 能力，Vidu AI 为创作者、影视团队和商业用户提供了高效视频创作解决方案。',
  },
  {
    question: 'Vidu 的参考生视频和图生视频有什么区别？',
    answer:
      '图生视频：允许用户从静态图片生成 AI 视频，让画面动起来，赋予其动画效果。用户还可以自定义视频的首帧和尾帧，优化视频的流畅度。这一功能大大简化了视频和动画制作过程，生成结果往往充满创意和惊喜。参考生视频：允许用户上传 3 张或以上的参考图片，Vidu 会根据提示词将多个视觉元素融合，生成一个无缝衔接的视频。用户还可以上传同一人物或物体的多个角度，确保视频中的主体一致性。这一功能适用于需要高度一致性的视频生成任务，完美融合多张参考图，实现自然流畅的动画效果。',
  },
  {
    question: 'Vidu 是免费的吗？',
    answer: '所有用户可获得一定数量的免费积分，无需付费即可生成视频。',
  },
  {
    question: 'Vidu AI 是否安全？',
    answer: '是的，Vidu AI 完全安全可用。我们高度重视用户数据安全和隐私，确保所有上传的内容都保持机密。',
  },
  {
    question: '如果我对 Vidu 有更多问题，应该如何联系你们？',
    answer:
      '您可以通过以下方式联系我们：加入我们的官方微信群社区，与其他用户和团队直接交流，获取最新动态和支持。或者请访问我们的帮助中心获取答案和支持。',
  },
];

export const FAQ_SEO_CONFIG = (
  t: ReturnType<typeof useTranslations>,
): I18nRecord<{ question: string; answer: string }[]> => {
  // 为其他语言生成通用的FAQ数据（使用QUESTIONS_CONFIG的逻辑）
  const generateFAQFromQuestions = () =>
    QUESTIONS_CONFIG(t).map((item) => ({
      question: item.question,
      answer: item.answer.map((answer) => answer.title).join(' '),
    }));

  // 动态生成除了 zh 和 en 之外的其他语言配置
  const dynamicLanguageConfig = GLOBAL_LANGUAGES.filter((lang) => lang !== 'zh' && lang !== 'en').reduce(
    (acc, lang) => {
      acc[lang] = generateFAQFromQuestions();
      return acc;
    },
    {} as Record<string, { question: string; answer: string }[]>,
  );

  return {
    china: {
      zh: FAQ_SEO_ZH,
    },
    global: {
      zh: FAQ_SEO_ZH,
      en: [
        {
          question: 'What is Vidu?',
          answer:
            'Vidu is a cutting-edge AI video generator that transforms text and images into high-quality videos, supporting multiple creation modes: Text to Video, Image to Video, and Reference to Video. Designed for independent creators and teams, Vidu simplifies the production of high-quality videos, 2D animations, and diverse artistic styles, empowering users to bring their creative visions to life effortlessly.With robust semantic understanding and fast generation speed, Vidu eliminates production complexities, allowing creators to focus solely on their ideas. From low resolution to stunning 1080p outputs, Vidu offers unmatched flexibility and efficiency, providing a broader canvas for creativity.',
        },
        {
          question: 'What is the use of Vidu AI?',
          answer:
            'Vidu AI is a powerful tool for creating high-quality AI-generated videos with exceptional consistency and fast generation speed. It is widely used across various industries, from social media content creation to professional video production and commercial advertising. Many creators use Vidu AI to produce viral videos like kissing, hugging, or fighting clips.Film studios and animation teams leverage it for studio-level 2D animations and cinematic content. Businesses also use Vidu AI to enhance marketing videos, such as image-to-video to animate product images or reference-to-video to change ad backgrounds, making video advertisements more dynamic and engaging.With its advanced AI capabilities, Vidu AI streamlines video production for creators, studios, and businesses.',
        },
        {
          question: "What's the difference between Vidu's Reference to Video and Image to Video?",
          answer:
            "Vidu's Image-to-Video feature allows users to create AI-generated videos from a static image, bringing them to life with motion and animation. Users can also set the first and last frames of their AI video. It streamlines the video or animation creation process—often leading to surprising and creative resultsReference to Video lets users upload three or more images of an object, character, or scene as references, and Vidu can combine elements into a seamless video based on a prompt. Vidu's Reference to Video also supports uploading different angles of a character or object to ensure consistency. The Reference-to-Video feature is ideal for maintaining consistency and seamlessly generating videos that blend multiple visual references.",
        },
        {
          question: 'Is Vidu AI free to use?',
          answer: 'All users receive a set amount of free credits to generate videos at no cost.',
        },
        {
          question: 'Is Vidu AI safe to use?',
          answer:
            'Yes, Vidu AI is completely safe to use. We prioritize user data security and privacy, ensuring all uploaded content remains confidential. Vidu does not share or leak personal information. Our system is designed with robust security measures to protect your data at all times.  For more details on how we handle your information, please refer to our Terms of Use.',
        },
        {
          question: 'How can I reach out if I have more questions about Vidu?',
          answer:
            'If you have additional questions about Vidu, you can: Join our Discord community at https://discord.gg/3pDU8fmQ8Y to connect with our team and other users for real-time support and updates. Visit our Help Center for answers and support.',
        },
      ],
      // 动态生成其他语言的FAQ配置
      ...dynamicLanguageConfig,
    },
  };
};
